import type { ITableMeta, IColumnMeta, IColumnConstraint } from '@/shared/types';

interface IRelationLine {
  readonly from: string;
  readonly to: string;
  readonly label: string;
}

function toSafeToken(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return 'unknown';
  const normalized = trimmed
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (normalized.length === 0) return 'unknown';
  return normalized;
}

function toEntityId(tableId: string): string {
  const safe = toSafeToken(tableId);
  return /^[0-9]/.test(safe) ? `T_${safe}` : safe;
}

function escapeMermaidLabel(value: string): string {
  return value.replace(/"/g, '\\"');
}

function buildFieldLine(column: IColumnMeta): string {
  const nameToken = toSafeToken(column.name);
  const typeToken = toSafeToken(column.dataType);
  const flags: string[] = [];
  if (column.isPrimaryKey) flags.push('PK');
  if (column.isForeignKey) flags.push('FK');
  const suffix = flags.length > 0 ? ` ${flags.join(' ')}` : '';
  return `    ${nameToken} ${typeToken}${suffix}`;
}

function collectRelations(table: ITableMeta): readonly IRelationLine[] {
  const relations: IRelationLine[] = [];
  const seen = new Set<string>();
  const from = toEntityId(table.id);

  table.columns.forEach((column: IColumnMeta) => {
    column.constraints.forEach((constraint: IColumnConstraint) => {
      if (constraint.type !== 'fk' || !constraint.referencedTable) return;
      const to = toEntityId(constraint.referencedTable);
      const referencedColumn =
        typeof constraint.referencedColumn === 'string' &&
        constraint.referencedColumn.trim().length > 0
          ? constraint.referencedColumn
          : '?';
      const label = escapeMermaidLabel(
        `${constraint.name} (${column.name} : ${referencedColumn})`,
      );
      const key = `${from}:${to}:${label}`;
      if (seen.has(key)) return;
      seen.add(key);
      relations.push({ from, to, label });
    });
  });

  return relations;
}

export function buildErDiagram(
  primaryTable: ITableMeta,
  relatedTables: readonly ITableMeta[],
): string {
  const tables = new Map<string, ITableMeta>();
  tables.set(primaryTable.id, primaryTable);
  relatedTables.forEach((table) => {
    tables.set(table.id, table);
  });

  const lines: string[] = ['erDiagram'];

  tables.forEach((table) => {
    const entityId = toEntityId(table.id);
    const label = escapeMermaidLabel(table.id);
    lines.push(`  ${entityId}["${label}"] {`);
    table.columns.forEach((column) => {
      lines.push(buildFieldLine(column));
    });
    lines.push('  }');
  });

  collectRelations(primaryTable).forEach((relation) => {
    lines.push(
      `  ${relation.from} }o--|| ${relation.to} : "${relation.label}"`,
    );
  });

  return lines.join('\n');
}
